import type { ChartMeta, ChartMetaDTO } from '@/features/chartsView';
import { baseApi, getErrorMessage } from '@/shared/api';
import type { CohortHistoryChartDataResponse } from '../model/types';
import type {
  DateFilterParamsDTO,
  GetCohortHistoryDataParams,
  GetDateFilterParams,
  GetNumberFilterParams,
  GetStringFilterParams,
} from './types';

export const getMeta = async (): Promise<ChartMeta> => {
  const { data, error } = await baseApi.functions.invoke<ChartMetaDTO>(
    'getComparisonViewCohortHistoryMeta',
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const getCohortHistoryChartData = async ({
  split,
  metrics,
  snapshotDateScale,
  snapshotDateRange,
  cohortDateScale,
  cohortDateRange,
  probabilisticAttribution,
  lifetime,
  dimensionsFilters,
  metricsFilters,
}: GetCohortHistoryDataParams): Promise<CohortHistoryChartDataResponse> => {
  const { data, error } =
    await baseApi.functions.invoke<CohortHistoryChartDataResponse>(
      'getComparisonViewCohortHistoryData',
      {
        body: {
          split,
          metrics,
          snapshotDateScale,
          snapshotDateRange,
          cohortDateScale,
          cohortDateRange,
          probabilisticAttribution,
          lifetime,
          dimensionsFilters,
          metricsFilters,
        },
      },
    );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const getStringFilterParams = async (
  params: GetStringFilterParams,
): Promise<{ options: string[] }> => {
  const { data, error } = await baseApi.functions.invoke<{
    options: string[];
  }>('getCohortMarketingHistoryStringFilterParams', {
    body: {
      filterName: params.filterName,
      dimensionsFilters: params.dimensionsFilters,
      snapshotDateRange: params.snapshotDateRange,
      snapshotDateScale: params.snapshotDateScale,
      cohortDateRange: params.cohortDateRange,
      cohortDateScale: params.cohortDateScale,
      probabilisticAttribution: params.probabilisticAttribution,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

export const getNumberFilterParams = async (
  params: GetNumberFilterParams,
): Promise<{ min: number; max: number }> => {
  const { data, error } = await baseApi.functions.invoke<{
    min: number;
    max: number;
  }>('getCohortMarketingHistoryNumberFilterParams', {
    body: {
      filterName: params.filterName,
      split: params.split,
      dimensionsFilters: params.dimensionsFilters,
      metricsFilters: params.metricsFilters,
      snapshotDateRange: params.snapshotDateRange,
      snapshotDateScale: params.snapshotDateScale,
      cohortDateRange: params.cohortDateRange,
      cohortDateScale: params.cohortDateScale,
      lifetime: params.lifetime,
      probabilisticAttribution: params.probabilisticAttribution,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

export const getDateFilterParams = async (
  params: GetDateFilterParams,
): Promise<DateFilterParamsDTO> => {
  const { data, error } = await baseApi.functions.invoke<DateFilterParamsDTO>(
    'getCohortMarketingHistoryDateFilterParams',
    {
      body: {
        filterName: params.filterName,
        probabilisticAttribution: params.probabilisticAttribution,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};
